import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Row} from "reactstrap";
import Dialog from "../../../base/dialog";
import UpsertPartSupportRequestDocumentation from "../_common/documentation";
import TopBarProgressIndicator from "../../../app-specific/topbar-progress-indicator";
import EditPartSupportRequestBasicInfo from "./sections/basic-info";
import EditPartSupportRequestProductInfo from "./sections/product-info";
import EditPartSupportRequestReview from "./sections/review";
import Api from "../../../../../core/services/api/api";
import useIsMounted from "../../../../hooks/use-is-mounted";

const initBasicInfo = {
    rga: undefined,
    customer: undefined,
    autoRga: false,
    inventoryBased: false,
    type: undefined,
    warehouse: undefined,
}

const initProductInfo = {
    showProductOrSerialNoSelection: false,
    invoiceNo: null,
    warehouseCode: null,
    part: undefined,
    isSerialized: false,
    serialNumber: undefined,
    selectedQuantity: null,
}


export const EditPartSupportRequestProcessSteps = {
    basicInfo: 'Basic Info',
    productInfo: 'Product Info',
    review: 'Review',
    documentation: 'Documentation',
}

const EditPartSupportRequestDialog = ({
                                          open,
                                          setOpen,
                                          onSuccess,
                                          types,
                                          loadingTypes,
                                          request,
                                      }) => {
    const [currentProcess, setCurrentProcess] = useState(EditPartSupportRequestProcessSteps.basicInfo);
    const [basicInfo, setBasicInfo] = useState(initBasicInfo);
    const [initialBasicInfo, setInitialBasicInfo] = useState(initBasicInfo);
    const [productInfo, setProductInfo] = useState(initProductInfo)
    const [newRequest, setNewRequest] = useState({})
    const [warehouses, setWarehouses] = useState([]);
    const [fetchingWarehouses, setFetchingWarehouses] = useState(false);
    const isMounted = useIsMounted();

    const warehouseChanged = useMemo(() => initialBasicInfo.warehouse?.code !== basicInfo.warehouse?.code, [basicInfo.warehouse, initialBasicInfo.warehouse])

    /**
     * Initializes the values of this dialog values as soon as the operator opens it.
     * @param {any} request
     * @param {any[]} warehouses
     */
    const initValues = useCallback((request, warehouses) => {
        const initBasicInfo = {
            rga: request.rga,
            customer: request.customer,
            autoRga: false,
            inventoryBased: !!request.customer
                ? false
                : !!request.warehouseCode,
            type: types.find(e => e.id === request.typeID),
            warehouse: !!request.customer
                ? null
                : warehouses?.find(e => e.code === request.warehouseCode),
        };
        setBasicInfo(initBasicInfo)
        setInitialBasicInfo(initBasicInfo);
    }, [types])

    /**
     * Fetches all the warehouses for the current partner.
     * @type {function(): Promise<void>}
     */
    const getAllWarehouses = useCallback(async () => {
        const response = await Api.getOperatorWarehouses()
        if (!isMounted())
            return;
        setFetchingWarehouses(false);
        if (response?.resultFlag) {
            setWarehouses(response?.data ?? []);
            initValues(request, response?.data ?? []);
        }
    }, [initValues, isMounted, request])


    /**
     * Resets the state of the modal as soon as the process successful finishes and calls onSuccess with the
     * returned request.
     * @type {function(*=): void}
     */
    const terminateProcess = useCallback((createdRequest = null) => {
        setCurrentProcess(EditPartSupportRequestProcessSteps.basicInfo);
        setBasicInfo(initBasicInfo)
        setProductInfo(initProductInfo)
        setNewRequest({})
        if (createdRequest) {
            onSuccess(createdRequest)
        }
        setOpen(false);
    }, [onSuccess, setOpen])

    /**
     * Listens for the changes in open and with each change:
     * - if not open then empties the list of duplicated RGAs.
     * - else, initializes the dialog values
     */
    useEffect(() => {
        if (!open) {
            return terminateProcess(null)
        }
        setFetchingWarehouses(true);
        getAllWarehouses().then();
    }, [getAllWarehouses, open, terminateProcess])

    /**
     * Determines if a ball has been passed over relative to their order in ordering process.
     * @param currentType {string}
     * @param type {string}
     * @return {boolean}
     */
    const passed = useCallback((currentType, type) => {
        switch (currentType) {
            case EditPartSupportRequestProcessSteps.basicInfo:
            default:
                return false
            case EditPartSupportRequestProcessSteps.productInfo:
                return (type === EditPartSupportRequestProcessSteps.basicInfo);
            case EditPartSupportRequestProcessSteps.review:
                return (type === EditPartSupportRequestProcessSteps.basicInfo || type === EditPartSupportRequestProcessSteps.productInfo);
            case EditPartSupportRequestProcessSteps.documentation:
                return (type === EditPartSupportRequestProcessSteps.basicInfo || type === EditPartSupportRequestProcessSteps.productInfo || type === EditPartSupportRequestProcessSteps.review);
        }
    }, [])

    return (
        <Dialog
            disableBackdropClick={currentProcess !== EditPartSupportRequestProcessSteps.basicInfo}
            open={open}
            setOpen={setOpen}
            className={'edit-warranty-and-return-dialog'}
            maxWidth={'md'}>
            <TopBarProgressIndicator
                types={EditPartSupportRequestProcessSteps}
                currentType={currentProcess}
                passed={passed}
                className={'dialog-top-section'}
            />
            <Row className='no-gutters justify-content-center align-items-start w-100 mt--4'>
                {
                    fetchingWarehouses &&
                    <div style={{height: 360, width: "100%"}}/>
                }
                {
                    !fetchingWarehouses &&
                    <>
                        {
                            currentProcess === EditPartSupportRequestProcessSteps.basicInfo &&
                            <EditPartSupportRequestBasicInfo
                                basicInfo={basicInfo}
                                setBasicInfo={setBasicInfo}
                                initialBasicInfo={initialBasicInfo}
                                loadingTypes={loadingTypes}
                                types={types}
                                setProcess={setCurrentProcess}
                                setOpen={setOpen}
                                warehouses={warehouses}
                            />
                        }
                        {
                            currentProcess === EditPartSupportRequestProcessSteps.productInfo &&
                            <EditPartSupportRequestProductInfo
                                warehouseChanged={warehouseChanged}
                                request={request}
                                productInfo={productInfo}
                                setProductInfo={setProductInfo}
                                setProcess={setCurrentProcess}
                                customer={basicInfo.customer}
                                warehouse={basicInfo.warehouse}
                            />
                        }
                        {
                            currentProcess === EditPartSupportRequestProcessSteps.review &&
                            <EditPartSupportRequestReview
                                request={request}
                                setNewRequest={setNewRequest}
                                setProcess={setCurrentProcess}
                                terminateProcess={terminateProcess}
                                basicInfo={basicInfo}
                                productInfo={productInfo}
                                setProductInfo={setProductInfo}
                                warehouses={warehouses}
                            />
                        }
                        {
                            currentProcess === EditPartSupportRequestProcessSteps.documentation &&
                            <UpsertPartSupportRequestDocumentation
                                warehouseCode={basicInfo.warehouse?.code}
                                terminateProcess={terminateProcess}
                                request={newRequest}
                            />
                        }
                    </>
                }
            </Row>
        </Dialog>
    );
}

export default EditPartSupportRequestDialog;
